import React, { memo } from 'react';
import cn from 'classnames';

import { Image, Link } from 'components';
import { Row } from 'components/layout';

import { ProfilePics } from 'components/candidate/ProfilePics/ProfilePics.component';
import Deadline from 'components/Deadline';
import Chip from 'components/Chip';
import { parseArrayResponse } from 'lib/utils/parser';
import { CitiesResponse } from 'lib/models/city';
import styles from './CommunityEventCard.module.scss';

export interface CommunityEventCardDataProps {
  id: string;
  title: string;
  start_date: string;
  url: string;
  avatar_urls: Array<string>;
  applications_count: number;
  event_icon: string;
  company_name: string;
  isExpired: boolean;
  application_deadline: string;
  cities: CitiesResponse | null;
}

export interface CommunityEventCardProps extends CommunityEventCardDataProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const CommunityEventCard: React.FC<CommunityEventCardProps> = memo(
  ({
    id,
    className = '', // custom class name
    style, // custom style
    title,
    start_date,
    url,
    avatar_urls,
    applications_count,
    event_icon,
    company_name,
    isExpired,
    application_deadline,
    cities,
  }: CommunityEventCardProps) => {
    const contentClassNames = cn(
      {
        [styles.CommunityEventCard]: true,
      },
      className
    );

    const location = cities ? parseArrayResponse(cities) : [];
    const maxPicsToShow = applications_count > 10 ? 10 : applications_count;

    const moreApplications =
      applications_count > maxPicsToShow ? (
        <div className={styles.chip}>
          + {applications_count - maxPicsToShow}
        </div>
      ) : (
        <></>
      );

    return (
      <Link
        href={url}
        underlineOnHover={false}
        className={styles.link}
        target="_blank"
      >
        <div
          className={contentClassNames}
          style={style}
          data-testid="CommunityEventCard"
        >
          <div className={styles.details}>
            <div>
              {isExpired ? (
                <Row className={styles.startDate}>{start_date}</Row>
              ) : null}
              <p className={styles.title}>{title}</p>
              <p className={styles.description}>{company_name}</p>
            </div>
            {event_icon && (
              <div className={styles.eventImage}>
                <Image src={event_icon} alt={`Cover Image of ${title}`} />
              </div>
            )}
          </div>
          {applications_count ? (
            <div
              className={cn(styles.subscribers, {
                [styles.gap]: applications_count > 10,
              })}
            >
              <div className={styles.subscribersContainer}>
                <ProfilePics urls={avatar_urls} maxPicsToShow={maxPicsToShow} />
                {moreApplications}
              </div>
              <div className={styles.subscribersCount}>
                {applications_count} students already registered to this event.
              </div>
            </div>
          ) : null}

          {!isExpired && !applications_count && application_deadline ? (
            <Row align="center" className={styles.detail}>
              {location.length > 0 ? (
                <Chip key={id} className={cn(styles.kind)}>
                  <span className={styles.name}>
                    📍 {location[0]?.name || 'London'}
                  </span>
                </Chip>
              ) : null}
              <Deadline
                application_deadline={application_deadline}
                className={cn(styles.chipDeadline)}
              />
            </Row>
          ) : null}
        </div>
      </Link>
    );
  }
);

CommunityEventCard.displayName = 'CommunityEventCard';
